/**
 * client/render/merge.ts — collapse a multi-material (skinned) model into ONE mesh drawing from texture
 * arrays (one draw call per player body / weapon instead of 4-10). OWNER: RENDER agent.
 * All primitives of a GLB node share one skeleton and bind matrix, so their geometries merge directly;
 * colour/normal maps are resampled to the largest texture size of the set (ESTIMATE-free: UVs are normalized).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TextureArrayBuilder, imageSize, resizeImage, type LayerSource } from './texarray';

const KEEP = ['position', 'normal', 'uv', 'skinIndex', 'skinWeight'];

export interface Layers { color: THREE.DataArrayTexture; normal: THREE.DataArrayTexture | null }
/** Layer textures by merged geometry (geometry is shared by clones; userData is JSON-cloned, so not used). */
const layerMap = new WeakMap<THREE.BufferGeometry, Layers>();
export function layersOf(g: THREE.BufferGeometry): Layers | undefined { return layerMap.get(g); }

/**
 * Replace every group of sibling meshes sharing a parent (and skeleton) inside `root` by one merged mesh with
 * an `aLayer` attribute; returns the merged meshes with their texture arrays in userData.layers.
 * Transparent (BLEND) primitives are left as they are.
 */
export async function mergeLayered(root: THREE.Object3D, renderer: THREE.WebGLRenderer, maxSize = 1024): Promise<THREE.Mesh[]> {
  const byParent = new Map<THREE.Object3D, THREE.Mesh[]>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !o.parent) return;
    const mat = m.material as THREE.MeshStandardMaterial;
    if (Array.isArray(m.material) || mat.transparent) return;
    const list = byParent.get(o.parent) ?? [];
    list.push(m);
    byParent.set(o.parent, list);
  });
  const out: THREE.Mesh[] = [];
  for (const [parent, meshes] of byParent) {
    if (meshes.length < 2) continue;
    const skinned = meshes.every((m) => (m as THREE.SkinnedMesh).isSkinnedMesh);
    if (!skinned && meshes.some((m) => (m as THREE.SkinnedMesh).isSkinnedMesh)) continue;
    let w = 1, h = 1;
    for (const m of meshes) {
      const img = (m.material as THREE.MeshStandardMaterial).map?.image;
      if (img) { const [iw, ih] = imageSize(img); w = Math.max(w, iw); h = Math.max(h, ih); }
    }
    w = Math.min(w, maxSize); h = Math.min(h, maxSize);
    const colors = new TextureArrayBuilder(w, h), normals = new TextureArrayBuilder(w, h);
    const geos: THREE.BufferGeometry[] = [];
    const jobs: Promise<void>[] = [];
    let anyNormal = false;
    for (const m of meshes) {
      const mat = m.material as THREE.MeshStandardMaterial;
      const layer = colors.count;
      const cimg = mat.map?.image as TexImageSource | undefined;
      const nimg = mat.normalMap?.image as TexImageSource | undefined;
      colors.add(m.uuid, { flat: [255, 255, 255, 255] });
      normals.add(m.uuid, { flat: [128, 128, 255, 255] });
      if (cimg) jobs.push(resizeImage(cimg, w, h).then((r) => { colors.layers[layer] = r as LayerSource; }));
      if (nimg) { anyNormal = true; jobs.push(resizeImage(nimg, w, h).then((r) => { normals.layers[layer] = r as LayerSource; })); }
      const g = m.geometry.clone();
      for (const k of Object.keys(g.attributes)) if (!KEEP.includes(k)) g.deleteAttribute(k);
      const n = g.attributes.position.count;
      g.setAttribute('aLayer', new THREE.BufferAttribute(new Float32Array(n).fill(layer), 1));
      if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
      if (!g.attributes.normal) g.computeVertexNormals();
      geos.push(g);
    }
    await Promise.all(jobs);
    const merged = mergeGeometries(geos, false);
    if (!merged) continue;
    const color = colors.build(renderer, 4);
    const normal = anyNormal ? normals.build(renderer, 4) : null;
    const first = meshes[0];
    const mesh = skinned ? new THREE.SkinnedMesh(merged, first.material) : new THREE.Mesh(merged, first.material);
    mesh.name = first.name + '_merged';
    mesh.position.copy(first.position); mesh.quaternion.copy(first.quaternion); mesh.scale.copy(first.scale);
    if (skinned) {
      const s = first as THREE.SkinnedMesh;
      (mesh as THREE.SkinnedMesh).bind(s.skeleton, s.bindMatrix);
    }
    layerMap.set(merged, { color, normal });
    for (const m of meshes) parent.remove(m);
    parent.add(mesh);
    out.push(mesh);
  }
  return out;
}
